import React, { useState, useEffect, useRef } from "react";
import { Button, Box, Typography, CircularProgress } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PersonIcon from "@mui/icons-material/Person";
import Image from "@mui/icons-material/Image";
import axios from "axios";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";
import { usePdfGenerator } from "../../hooks/usePdfGenerator";
import { useApplicationPhotoInfo, useApplicationPhoto } from "../../hooks/useApplicationForm";
import { useStudentPhoto, useStudentSignature } from "../../hooks/useStudents";
import ReactDOMServer from "react-dom/server";
import { useTranslation } from "react-i18next";
import { Download, Image as ImageIcon } from "@mui/icons-material";
import { TENTH_CLASS_SUBJECTS } from "../enum/SubjectOptions";

// Form sections are now dynamically generated based on selected subjects

const ApplicationFormTemplate = ({
  data,
  schoolData,
  renderForPDF = false,
  preloadedSubjectNames = {}, // New prop for pre-loaded subject names
}: {
  data: any;
  schoolData?: any;
  renderForPDF?: boolean;
  preloadedSubjectNames?: { [key: string]: string }; // New prop type
}) => {
  const { t } = useTranslation();
  const [subjectNames, setSubjectNames] = useState<{ [key: string]: string }>(
    preloadedSubjectNames
  );
  const [loadingSubjects, setLoadingSubjects] = useState<{
    [key: string]: boolean;
  }>({});
  const [allSubjectsLoaded, setAllSubjectsLoaded] = useState(false);
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdfFromImage, setIsDownloadingPdfFromImage] =
    useState(false);
  const [captureMode, setCaptureMode] = useState(false);
  const [imageError, setImageError] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);
  const isPrint = captureMode || renderForPDF;
  const { generatePdf, isGenerating, error } = usePdfGenerator();

  // Get application photo info if we have an application ID
  const applicationId = data?.id;

  // Determine if this is an application form by checking for application-specific fields
  // Application forms ALWAYS have applicationNo field - this is the definitive indicator
  // Students don't have applicationNo field
  const hasApplicationNo = !!data?.applicationNo;

  // Determine student ID - check multiple possible fields
  // When editing a student, data.id is the student ID and data has rollNo/rollNumber
  // Check for student indicators: rollNo, rollNumber, or isRegistered === "Approved" (capital A)
  // BUT: Only consider it a student if it doesn't have applicationNo (to avoid false positives)
  const hasRollNo = !!(data?.rollNo || data?.rollNumber);
  const isApproved = data?.isRegistered === "Approved" || data?.isRegistered === "approved";
  // Only treat as student if it has student indicators AND doesn't have applicationNo
  const isLikelyStudent = (hasRollNo || isApproved) && !hasApplicationNo;

  // If it's likely a student, use data.id as studentId (since student.id is passed as data.id when editing)
  // Otherwise, check for explicit studentId field
  const studentId = data?.studentId || (isLikelyStudent && data?.id ? data.id : undefined);

  // For application forms: Always use application photo API
  // For students: Use student photo API
  // If studentId is explicitly passed in data, always treat as student (use student photo API)
  // If it has applicationNo, it's DEFINITELY an application form - use application photo API
  // Otherwise, if it has applicationId but doesn't look like a student, treat as application form
  const hasExplicitStudentId = !!data?.studentId;
  const isApplicationForm = !hasExplicitStudentId && applicationId && (hasApplicationNo || (!isLikelyStudent && !hasRollNo));

  // Fetch application photo (for application forms only)
  const {
    data: applicationPhotoInfo,
    isLoading: applicationPhotoLoading,
    error: applicationPhotoError,
  } = useApplicationPhotoInfo(isApplicationForm ? applicationId : undefined);

  // Fetch application photo blob URL (for application forms only)
  const {
    data: applicationImageUrl,
    isLoading: applicationPhotoBlobLoading,
    error: applicationPhotoBlobError,
  } = useApplicationPhoto(applicationPhotoInfo?.fileName);

  // Fetch application signature (for application forms only)
  const {
    data: applicationSignatureUrl,
    isLoading: applicationSignatureBlobLoading,
    error: applicationSignatureBlobError,
  } = useApplicationPhoto(applicationPhotoInfo?.studentSignature);

  // Fetch student photo (ONLY for actual students, not application forms)
  // Allow fetching if: hasExplicitStudentId (StudentProfile passing studentId), OR it's a student without applicationNo
  const shouldFetchStudentPhoto = (hasExplicitStudentId || (!hasApplicationNo && isLikelyStudent)) && !isApplicationForm;
  const {
    imageUrl: studentImageUrl,
    isLoading: studentPhotoLoading,
    error: studentPhotoError,
    hasPhoto: hasStudentPhoto,
  } = useStudentPhoto(shouldFetchStudentPhoto ? studentId : undefined);

  // Fetch student signature (ONLY for actual students, not application forms)
  // Allow fetching if: hasExplicitStudentId (StudentProfile passing studentId), OR it's a student without applicationNo
  const shouldFetchStudentSignature = (hasExplicitStudentId || (!hasApplicationNo && isLikelyStudent)) && !isApplicationForm;
  const {
    signatureUrl: studentSignatureUrl,
    isLoading: studentSignatureLoading,
    error: studentSignatureError,
    hasSignature: hasStudentSignature,
  } = useStudentSignature(shouldFetchStudentSignature ? studentId : undefined);

  // Check if there's a direct photo URL in the data (fallback)
  const directPhotoUrl =
    data?.studentPhoto ||
    data?.photo ||
    data?.imageUrl ||
    data?.photoUrl ||
    data?.image;

  // Determine the final image URL to use
  // For application forms: prioritize application photo, then direct URL
  // For students: prioritize student photo, then direct URL
  const imageUrl = isApplicationForm
    ? (applicationImageUrl || directPhotoUrl || null)
    : (studentImageUrl || directPhotoUrl || null);

  // Determine the final signature URL to use
  // For application forms: use application signature URL
  // For students: use student signature URL
  const signatureUrl = isApplicationForm
    ? (applicationSignatureUrl || null)
    : (studentSignatureUrl || null);

  // Determine signature loading state
  const isLoadingSignature = isApplicationForm
    ? applicationSignatureBlobLoading
    : studentSignatureLoading;

  // Determine signature error state
  const hasSignatureError = isApplicationForm
    ? !!applicationSignatureBlobError
    : !!studentSignatureError;

  // Check for actual errors
  // studentPhotoError is a boolean from useStudentPhoto hook (true = error, false = no error)
  // applicationPhotoError and applicationPhotoBlobError are error objects from React Query (null = no error)
  const hasPhotoError = !!(applicationPhotoError || applicationPhotoBlobError || studentPhotoError);

  // Only show loader if we're actually loading a photo that exists
  // Show loader if: student photo is loading (and exists), OR application photo blob is loading (and photoInfo exists)
  const isLoadingPhoto =
    (hasStudentPhoto && studentPhotoLoading) ||
    (applicationPhotoInfo?.fileName && applicationPhotoBlobLoading);

  // Reset imageError when imageUrl changes to allow retry
  useEffect(() => {
    if (imageUrl) {
      setImageError(false);
    }
  }, [imageUrl]);


  // Function to download template as image
  const downloadAsImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingImage(true);
    setCaptureMode(true);
    try {
      await (document as any).fonts?.ready?.catch(() => { });
      await new Promise(r => setTimeout(r, 80));
      const canvas = await html2canvas(templateRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        background: "#ffffff",
        logging: false,
        scale: 2,
        width: templateRef.current.scrollWidth,
        height: templateRef.current.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: templateRef.current.scrollWidth,
        windowHeight: templateRef.current.scrollHeight,
      } as any);
      const link = document.createElement("a");
      link.download = `application-form-${data?.studentName || data?.firstName || "student"}-${new Date().toISOString().split("T")[0]}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
    } catch (e) {
      alert("Failed to download image. Please try again.");
    } finally {
      setCaptureMode(false);
      setIsDownloadingImage(false);
    }
  };

  // Function to download template as PDF directly from HTML
  const downloadAsPdfFromImage = async () => {
    if (!templateRef.current) return;
    setIsDownloadingPdfFromImage(true);
    setCaptureMode(true);
    try {
      await (document as any).fonts?.ready?.catch(() => { });
      await new Promise(r => setTimeout(r, 80));
      const element = templateRef.current;
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `application-form-${data?.studentName || data?.firstName || "student"}-${new Date().toISOString().split("T")[0]}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 1,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          logging: false,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "portrait",
          compress: false,
          putOnlyUsedFonts: false,
        },
        pagebreak: { mode: ["css", "legacy"], before: ".page-break" },
      };
      await html2pdf().set(opt).from(element).save();
    } catch (e) {
      alert("Failed to download PDF. Please try again.");
    } finally {
      setCaptureMode(false);
      setIsDownloadingPdfFromImage(false);
    }
  };

  let fieldCounter = 1;

  // Helper function to get subjects from application form data
  const getSubjectsFromData = (data: any) => {
    if (!data) return [];

    // Handle different possible subject field names
    if (data.subjects && Array.isArray(data.subjects)) {
      return data.subjects;
    }

    // Fallback to individual subject fields if subjects array is not available
    const individualSubjects = [];
    for (let i = 1; i <= 6; i++) {
      const subjectKey = `subject${i}`;
      if (data[subjectKey]) {
        individualSubjects.push(data[subjectKey]);
      }
    }

    if (individualSubjects.length > 0) {
      return individualSubjects;
    }

    return [];
  };

  // Get the actual subjects selected by the student
  const selectedSubjects = getSubjectsFromData(data);

  // Function to fetch subject details from API
  const fetchSubjectName = async (subjectCode: string) => {
    if (subjectNames[subjectCode] || loadingSubjects[subjectCode]) {
      return; // Already fetched or fetching
    }

    setLoadingSubjects((prev) => ({ ...prev, [subjectCode]: true }));

    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/subjects/code/${subjectCode}`;
      const response = await axios.get(apiUrl);
      const subjectData = response.data;

      if (subjectData && subjectData.name) {
        setSubjectNames((prev) => ({
          ...prev,
          [subjectCode]: `${subjectData.name}(${subjectCode})`,
        }));
      } else {
        // Fallback to SubjectOptions if no name in response
        const subjectOption = TENTH_CLASS_SUBJECTS.find((s) => s.code === subjectCode);
        if (subjectOption) {
          setSubjectNames((prev) => ({
            ...prev,
            [subjectCode]: `${subjectOption.name}(${subjectCode})`,
          }));
        } else {
          setSubjectNames((prev) => ({
            ...prev,
            [subjectCode]: `Subject(${subjectCode})`,
          }));
        }
      }
    } catch (error) {
      // Fallback to SubjectOptions if API fails
      const subjectOption = TENTH_CLASS_SUBJECTS.find((s) => s.code === subjectCode);
      if (subjectOption) {
        setSubjectNames((prev) => ({
          ...prev,
          [subjectCode]: `${subjectOption.name}(${subjectCode})`,
        }));
      } else {
        setSubjectNames((prev) => ({
          ...prev,
          [subjectCode]: `Subject(${subjectCode})`,
        }));
      }
    } finally {
      setLoadingSubjects((prev) => ({ ...prev, [subjectCode]: false }));
    }
  };

  // Function to get subject name as string (for table display)
  const getSubjectNameString = (subjectCode: string): string => {
    // If we have the subject name from API or preloaded, use it
    if (subjectNames[subjectCode]) {
      return subjectNames[subjectCode];
    }

    // If not fetched yet, trigger fetch and show loading
    if (!loadingSubjects[subjectCode]) {
      fetchSubjectName(subjectCode);
    }

    // Show loading or fallback
    if (loadingSubjects[subjectCode]) {
      return `Loading...`;
    }

    // Fallback to SubjectOptions for unknown subject codes
    const subjectOption = TENTH_CLASS_SUBJECTS.find((s) => s.code === subjectCode);
    if (subjectOption) {
      return `${subjectOption.name}(${subjectCode})`;
    }

    // Final fallback
    return `Subject(${subjectCode})`;
  };

  // Check if all subjects are loaded
  useEffect(() => {
    if (selectedSubjects.length > 0) {
      const allLoaded = selectedSubjects.every(
        (subjectCode: string) =>
          subjectCode &&
          subjectNames[subjectCode] &&
          !loadingSubjects[subjectCode]
      );
      setAllSubjectsLoaded(allLoaded);
    } else {
      setAllSubjectsLoaded(true); // No subjects to load
    }
  }, [selectedSubjects, subjectNames, loadingSubjects]);

  // Update subjectNames when preloadedSubjectNames prop changes
  useEffect(() => {
    if (Object.keys(preloadedSubjectNames).length > 0) {
      setSubjectNames(preloadedSubjectNames);
    }
  }, [preloadedSubjectNames]);

  // Create dynamic form sections
  const createFormSections = () => {
    const baseSections = [
      {
        title: "", // Empty title to hide section header
        titleHindi: "", // Empty titleHindi to hide section header
        fields: [
          {
            hindi: "आवेदन क्रमांक",
            english: "APPLICATION NO",
            key: "applicationNo",
          },
          { hindi: "रोल नंबर", english: "ROLL NO.", key: "rollNo" },
          {
            hindi: "नामांकन नंबर",
            english: "ENROLMENT NO",
            key: "enrolmentNo",
          },
          { hindi: "स्कॉलर नंबर", english: "SCHOLAR NO.", key: "scholarNo" },
          { hindi: "समग्र आईडी", english: "SSSMID", key: "sssmid" },
          { hindi: "पेन नंबर", english: "PEN NO.", key: "penNo" },
          {
            hindi: "एपार आईडी नंबर",
            english: "APPAR ID NO.",
            key: "apparIdNo",
          },
          {
            hindi: "सीडब्ल्यूएसएन कोड",
            english: "CWSN CODE",
            key: "cwsnCode",
          },
          {
            hindi: "संबल क्रमांक",
            english: "SAMBAL KRAMANK",
            key: "sambalKramank",
          },
        ],
      },
      {
        title: "", // Empty title to hide section header
        titleHindi: "", // Empty titleHindi to hide section header
        fields: [
          {
            hindi: "छात्र/छात्रा का नाम",
            english: "STUDENT NAME",
            key: "studentName",
          },
          { hindi: "पिता का नाम", english: "FATHER'S NAME", key: "fatherName" },
          { hindi: "माता का नाम", english: "MOTHER'S NAME", key: "motherName" },
          { hindi: "जन्म तिथि", english: "DATE OF BIRTH", key: "dateOfBirth" },
          { hindi: "लिंग", english: "GENDER (M/F)", key: "gender" },
          { hindi: "धर्म", english: "RELIGION", key: "religion" },
          { hindi: "जाति", english: "CASTE", key: "caste" },
          { hindi: "उप जाति", english: "SUB CASTE", key: "subCaste" },
          {
            hindi: "जाति प्रमाण पत्र",
            english: "CASTE CERTIFICATE (YES/NO/GEN)",
            key: "casteCertificate",
          },
          { hindi: "आधार नंबर", english: "AADHAR NO", key: "aadharNo" },
        ],
      },
      {
        title: "", // Empty title to hide section header
        titleHindi: "", // Empty titleHindi to hide section header
        fields: [
          {
            hindi: "घर नंबर/बिल्डिंग/अपार्टमेंट",
            english: "HOUSE NO / BUILDING / APARTMENT",
            key: "houseNo",
          },
          {
            hindi: "गली/क्षेत्र/सेक्टर",
            english: "STREET / LOCALITY / SECTOR",
            key: "street",
          },
          { hindi: "लैंडमार्क", english: "LANDMARK", key: "landmark" },
          {
            hindi: "क्षेत्र/स्थानीयता/सेक्टर",
            english: "AREA / LOCALITY / SECTOR",
            key: "area",
          },
          {
            hindi: "गांव/शहर/नगर",
            english: "VILLAGE / TOWN / CITY",
            key: "village",
          },
          { hindi: "डाकघर", english: "POST OFFICE", key: "postOffice" },
          { hindi: "जिला", english: "DISTRICT", key: "district" },
          { hindi: "राज्य", english: "STATE", key: "state" },
          { hindi: "पिन कोड", english: "PIN CODE", key: "pinCode" },
          { hindi: "मोबाइल नंबर", english: "MOBILE NO", key: "mobileNo" },
          { hindi: "जीमेल", english: "GMAIL", key: "gmail" },
        ],
      },
      {
        title: "", // Empty title to hide section header
        titleHindi: "", // Empty titleHindi to hide section header
        fields: [
          { hindi: "बैंक का नाम", english: "BANK NAME", key: "bankName" },
          { hindi: "आईएफएससी कोड", english: "IFSC CODE", key: "ifcCode" },
          { hindi: "खाता नंबर", english: "ACCOUNT NO", key: "accountNo" },
          {
            hindi: "वार्षिक आय",
            english: "ANNUAL INCOME",
            key: "annualIncome",
          },
        ],
      },
      {
        title: "", // Empty title to hide section header
        titleHindi: "", // Empty titleHindi to hide section header
        fields: [
          {
            hindi: "प्रवेश तिथि",
            english: "ADMISSION DATE",
            key: "admissionDate",
          },
          {
            hindi: "प्रवेश प्रकार",
            english: "ADMISSION TYPE",
            key: "admissionType",
          },
          {
            hindi: "प्रवेश कक्षा",
            english: "ADMISSION CLASS",
            key: "admissionClass",
          },
          { hindi: "सेक्शन", english: "SECTION", key: "section" },
          { hindi: "माध्यम", english: "MEDIUM", key: "medium" },
        ],
      },
    ];

    // Create dynamic subjects section based on actual selected subjects
    const subjectsSection = {
      title: "", // Empty title to hide section header
      titleHindi: "", // Empty titleHindi to hide section header
      fields: selectedSubjects.map((subject: string, index: number) => {
        return {
          hindi: `विषय ${index + 1}`, // विषय 1, विषय 2, विषय 3, etc.
          english: `SUBJECT${index + 1}`, // SUBJECT1, SUBJECT2, SUBJECT3, etc.
          key: `subject${index + 1}`,
          value: subject, // Store the actual subject value
        };
      }),
    };

    return [...baseSections, subjectsSection];
  };

  const formSections = createFormSections();

  // Fetch subject names when component mounts or subjects change (only if not preloaded)
  useEffect(() => {
    // Only fetch if we don't have preloaded names
    if (Object.keys(preloadedSubjectNames).length === 0) {
      selectedSubjects.forEach((subjectCode: string) => {
        if (
          subjectCode &&
          !subjectNames[subjectCode] &&
          !loadingSubjects[subjectCode]
        ) {
          fetchSubjectName(subjectCode);
        }
      });
    }
  }, [
    selectedSubjects,
    subjectNames,
    loadingSubjects,
    fetchSubjectName,
    preloadedSubjectNames,
  ]);

  return (
    <div
      ref={templateRef}
      data-pdf-root
      style={{
        padding: 16,
        fontFamily: "Arial, sans-serif",
        fontSize: 12,
        lineHeight: 1.2,
        maxWidth: "840px",
        margin: "0 auto",
        backgroundColor: "white",
      }}
    >
      {/* Header Section */}
      <div
        className="header-section"
        style={{
          textAlign: "left",
          marginBottom: 4,
          border: "1px solid black",
          padding: 8,
          // display: "inline-block",
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", margin: 8 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: "bolder" }}>
              To The Principal,
            </h1>
          </div>
          <div
            style={{ display: "flex", flexDirection: "column", width: "100%" }}
          >
            {/* Left side - Name of School */}
            <div style={{ textAlign: "left", marginRight: "20px" }}>
              <p
                style={{
                  margin: "1px 0",
                  fontWeight: "bold",
                  fontSize: "20px",
                  whiteSpace: "nowrap",
                }}
              >
                <strong></strong>{" "}
                {schoolData?.nameofSchool ||
                  "GOVT H.S. SCHOOL VIJAYNAGAR INDORE"}
              </p>
            </div>
            {/* Right side - District */}
            <div style={{ textAlign: "left" }}>
              <p
                style={{
                  margin: "1px 0",
                  fontWeight: "bold",
                  fontSize: "16px",
                  whiteSpace: "nowrap",
                }}
              >
                <strong>District:</strong> {schoolData?.district || "INDORE"}
              </p>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            minWidth: "fit-content",
          }}
        >
          <div>
            {/* Student Photo Section */}
            {applicationId && (
              <div
                style={{
                  // marginBottom: 15,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    border: "1px solid black",
                    padding: 4,
                    display: "flex",
                    width: "120px",
                    height: "150px",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  {(() => {
                    // Show image if available
                    if (imageUrl && !hasPhotoError && !imageError) {
                      return (
                        <img
                          src={imageUrl}
                          alt="Student Photo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "2px",
                            display: "block",
                          }}
                          onError={() => setImageError(true)}
                          onLoad={() => setImageError(false)}
                        />
                      );
                    }

                    // Show loader only if we're actually loading a photo that exists
                    // (applicationPhotoInfo has fileName, meaning photo exists, and we're loading the blob)
                    if (isLoadingPhoto && (applicationPhotoInfo?.fileName || hasStudentPhoto)) {
                      return (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <CircularProgress size={20} />
                          <span style={{ fontSize: "8px", marginTop: "4px" }}>
                            Loading...
                          </span>
                        </div>
                      );
                    }

                    // Show "No Photo" when there's no image
                    return (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          color: "#666",
                        }}
                      >
                        <PersonIcon sx={{ fontSize: 40, color: "#ccc" }} />
                        <span
                          style={{
                            fontSize: "8px",
                            marginTop: "4px",
                            textAlign: "center",
                          }}
                        >
                          No Photo
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Table */}
      <table
        style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}
      >
        <thead>
          <tr>
            <th
              style={{
                border: "1px solid black",
                background: "#87CEEB",
                padding: "12px",
                textAlign: "center",
                fontWeight: "bold",
                width: "6%",
                fontSize: "14px",
              }}
            >
              क्र.
            </th>
            <th
              style={{
                border: "1px solid black",
                background: "#87CEEB",
                padding: "12px",
                textAlign: "center",
                fontWeight: "bold",
                width: "25%",
                fontSize: "14px",
              }}
            >
              हिंदी में विवरण
            </th>
            <th
              style={{
                border: "1px solid black",
                background: "#F5DEB3",
                padding: "12px",
                textAlign: "center",
                fontWeight: "bold",
                width: "27%",
                fontSize: "14px",
              }}
            >
              ENGLISH
            </th>
            <th
              style={{
                border: "1px solid black",
                background: "#F5DEB3",
                padding: "12px",
                textAlign: "center",
                fontWeight: "bold",
                width: "42%",
                fontSize: "14px",
              }}
            >
              VALUE
            </th>
          </tr>
        </thead>
        <tbody>
          {formSections.map((section, sectionIdx) => (
            <React.Fragment key={sectionIdx}>
              {/* Section Header - Only show if title exists */}
              {section.title && section.titleHindi && (
                <tr>
                  <td
                    colSpan={4}
                    style={{
                      border: "1px solid black",
                      background: "#E6E6FA",
                      padding: "10px",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "14px",
                    }}
                  >
                    {section.titleHindi} / {section.title}
                  </td>
                </tr>
              )}
              {/* Section Fields */}
              {section.fields.map((field: any, fieldIdx: number) => {
                const currentFieldNumber = fieldCounter;
                fieldCounter++;

                return (
                  <tr
                    key={`${sectionIdx}-${fieldIdx}`}
                    className={currentFieldNumber === 24 ? "page-break" : ""}
                    style={currentFieldNumber === 24 ? { pageBreakBefore: "always" } : {}}
                  >
                    <td
                      style={{
                        border: "1px solid black",
                        padding: "8px",
                        textAlign: "center",
                        fontWeight: "bold",
                        backgroundColor: "#fff",
                        fontSize: "14px",
                      }}
                    >
                      {currentFieldNumber}
                    </td>
                    <td
                      style={{
                        border: "1px solid black",
                        padding: "8px",
                        backgroundColor: "#6ec6fa",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      {field.hindi}
                    </td>
                    <td
                      style={{
                        border: "1px solid black",
                        padding: "8px",
                        backgroundColor: "#fbeee6",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      {field.english}
                    </td>
                    <td
                      style={{
                        border: "1px solid black",
                        padding: "8px",
                        minHeight: "20px",
                        backgroundColor: "#fbeee6",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        fontSize: "14px",
                      }}
                    >
                      {(() => {
                        // Handle special field mappings
                        switch (field.key) {
                          case "studentName": {
                            // Try to get student name from various possible fields
                            if (data.studentName) return data.studentName;
                            if (data.firstName && data.lastName) {
                              return `${data.firstName} ${data.lastName}`.trim();
                            }
                            if (data.firstName) return data.firstName;
                            if (data.lastName) return data.lastName;
                            return "";
                          }

                          case "apparIdNo": {
                            // Try to get appar ID from various possible fields
                            return data.apparIdNo || data.apparId || "";
                          }

                          case "village": {
                            // Try to get village from various possible fields
                            return data.village || data.city || "";
                          }

                          case "mobileNo": {
                            // Try to get mobile number from various possible fields
                            return data.mobileNo || data.phone || "";
                          }

                          default:
                            // For subject fields, use the stored value
                            if (field.value) {
                              // For subject fields, return subject name as string
                              if (field.key.startsWith("subject")) {
                                return getSubjectNameString(field.value);
                              }
                              return field.value;
                            }
                            // For all other fields, use the key directly
                            return data[field.key] || "";
                        }
                      })()}
                    </td>
                  </tr>
                );
              })}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {/* Signature Section */}
      <div
        className="signature-section"
        style={{
          marginTop: 10,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          padding: "0 20px",
        }}
      >
        {/* Left Section - Student Signature */}
        <div
          style={{
            width: "45%",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            minHeight: "120px",
          }}
        >
          {/* Signature Image */}
          <div
            style={{
              width: "200px",
              height: "60px",
              border: "1px solid #ccc",
              marginBottom: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {(() => {
              // Check if there's actually a signature to load
              const hasSignatureToLoad = isApplicationForm
                ? !!applicationPhotoInfo?.studentSignature
                : hasStudentSignature;

              // Only show loading if we're loading AND there's a signature to load
              if (isLoadingSignature && hasSignatureToLoad) {
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    }}
                  >
                    <CircularProgress size={16} />
                    <span style={{ fontSize: "8px", marginTop: "4px" }}>
                      Loading...
                    </span>
                  </div>
                );
              }

              // Show image if signature URL exists and no error
              if (signatureUrl && !hasSignatureError) {
                return (
                  <img
                    src={signatureUrl}
                    alt={isApplicationForm ? "Application Signature" : "Student Signature"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                );
              }

              // Show empty box if no signature found
              return (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {/* Empty signature box - will show dotted line below */}
                </div>
              );
            })()}
          </div>
          <div
            style={{
              borderBottom: "2px dotted #000",
              width: "40%",
              margin: "0 auto 10px auto",
              height: "1px",
            }}
          ></div>
          <p
            style={{
              margin: 0,
              fontSize: "12px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Signature of Student
          </p>
        </div>

        {/* Right Section - Parents Signature */}
        <div
          style={{
            width: "45%",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            minHeight: "120px",
          }}
        >
          {/* Signature Box - matching student signature box */}
          <div
            style={{
              width: "200px",
              height: "60px",
              border: "1px solid #ccc",
              marginBottom: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Empty signature box for parents */}
          </div>
          <div
            style={{
              borderBottom: "2px dotted #000",
              width: "40%",
              margin: "0 auto 10px auto",
              height: "1px",
            }}
          ></div>
          <p
            style={{
              margin: 0,
              fontSize: "12px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            Signature of Parents
          </p>
        </div>
      </div>

      {/* Download Buttons (hidden when renderForPDF is true) */}
      {!isPrint && (
        <Box
          sx={{ marginTop: "16px", textAlign: "center" }}
          className="no-print action-buttons-container"
        >
          <Box sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            justifyContent: "center",
          }}>
            {/* Image Download Button */}
            <Button
              variant="contained"
              startIcon={<ImageIcon />}
              disabled={isDownloadingImage || !allSubjectsLoaded}
              onClick={downloadAsImage}
              disableRipple
              sx={{
                bgcolor: "#1976d2",
                "&:hover": {
                  bgcolor: "#1976d2",
                  boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)"
                }
              }}
            >
              {!allSubjectsLoaded ? (
                "Loading Subjects..."
              ) : isDownloadingImage ? (
                "Downloading..."
              ) : (
                "Download as Image"
              )}
            </Button>

            {/* PDF Download Button */}
            <Button
              variant="contained"
              startIcon={<Download />}
              disabled={isDownloadingPdfFromImage || !allSubjectsLoaded}
              onClick={downloadAsPdfFromImage}
              disableRipple
              sx={{
                bgcolor: "#d32f2f",
                "&:hover": {
                  bgcolor: "#d32f2f",
                  boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)"
                }
              }}
            >
              {!allSubjectsLoaded ? (
                "Loading Subjects..."
              ) : isDownloadingPdfFromImage ? (
                "Downloading..."
              ) : (
                "Download as PDF"
              )}
            </Button>
          </Box>

          {!allSubjectsLoaded && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Please wait for all subject names to load before generating documents
            </Typography>
          )}

        </Box>
      )}

      {/* Loading progress indicators - Show when buttons are hidden during download */}
      {!isPrint && (isDownloadingImage || isDownloadingPdfFromImage) && (
        <Box sx={{ marginTop: "16px", textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {isDownloadingImage
              ? "Please wait while your application form image is being generated..."
              : "Please wait while your application form PDF is being generated..."
            }
          </Typography>
        </Box>
      )}

      {/* Print-specific styles */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @media print {
              .no-print, .action-buttons-container {
                display: none !important;
              }
              body {
                -webkit-print-color-adjust: exact;
              }
              /* Ensure form fits on pages properly */
              @page {
                size: A4;
                margin: 10mm;
              }
              /* Page break control */
              .page-break {
                page-break-before: always !important;
              }
              /* Allow table to break across pages */
              table {
                page-break-inside: auto;
              }
              tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
              /* Header section should stay with first page content */
              .header-section {
                page-break-after: avoid;
              }
              /* Signature section styling for print */
              .signature-section {
                page-break-inside: avoid;
                margin-top: 20px !important;
              }
              /* Reduce font size for better fit */
              body {
                font-size: 10px;
                line-height: 1.1;
              }
              /* Compact spacing */
              div {
                margin: 5px 0;
              }
              h1 {
                font-size: 18px;
                margin: 5px 0;
              }
              table {
                font-size: 9px;
              }
              th, td {
                padding: 3px;
              }
            }
         `,
        }}
      />
    </div>
  );
};

export default ApplicationFormTemplate;
